import React, { useEffect, useState, createElement, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, MessageSquare, Calendar, Mail, Globe, FileText, Home, ChevronDown, Menu, ArrowRight, Shield, Zap } from 'lucide-react';
// --- Translations Dictionary ---
const translations = {
  en: {
    seo: {
      title: 'Order OpenClaw Installation Turnkey — AI Agent Setup for Business',
      description: 'Order OpenClaw installation turnkey for Telegram, WhatsApp, Slack, email, calendar, and files. AI agent setup for business tasks, launch, configuration, and training.',
      h1: 'Order OpenClaw Installation Turnkey',
      h1sub: 'We set up OpenClaw AI agent for Telegram, WhatsApp, Slack, email, calendar, browser, and files tailored to your business tasks.'
    },
    nav: {
      features: 'Features',
      howItWorks: 'How it works',
      pricing: 'Pricing',
      faq: 'FAQ',
      order: 'Order Setup'
    },
    hero: {
      title1: 'Order',
      title2: 'OpenClaw',
      title3: 'Installation Turnkey',
      subtitle: 'Your personal digital employee in Telegram, WhatsApp, or Slack — configured, trained, and ready to work 24/7.',
      btn: 'Order AI Agent Setup'
    },
    whatWeDo: {
      title1: 'You get a ready-to-use AI agent.',
      title2: 'We handle the rest.',
      withoutYou: 'Without you',
      withoutYouDesc: 'Reading complex docs, setting up server environments, messing with API keys, and writing system prompts.',
      we: 'We',
      weDesc: 'Install the core, configure integrations, connect messengers, train the agent for your specific tasks, and test it.',
      you: 'You',
      youDesc: 'Just open Telegram, text your new employee a task — and they get to work.',
      link: 'I want a ready agent'
    },
    features: {
      title1: 'What OpenClaw will do for your business',
      title2: 'right now',
      mail: 'Email Management',
      mailDesc: 'The agent reads, sorts, and drafts replies to routine emails on its own.',
      cal: 'Calendar',
      calDesc: 'Schedules meetings while keeping your availability in mind.',
      web: 'Browser',
      webDesc: 'Searches for info, scrapes websites, and fills out forms.',
      msg: 'Messengers',
      msgDesc: 'AI agent installation for Telegram, WhatsApp, Slack, and Discord. Chat with the agent like a coworker.',
      files: 'Files',
      filesDesc: 'Analyzes PDFs, spreadsheets, and generates reports.',
      home: 'Smart Home',
      homeDesc: 'Controls lights and automation routines.',
      link: 'Order OpenClaw installation'
    },
    how: {
      title1: 'From request to a working agent —',
      title2: 'turnkey',
      s1: 'You leave a request',
      s1d: 'Fill out a short form or text us on Telegram.',
      s2: 'Free consultation',
      s2d: 'We hop on a call to understand your tasks and workflows.',
      s3: 'OpenClaw setup for business',
      s3d: 'We deploy OpenClaw, connect the required channels, and train the agent.',
      s4: 'Handover & Onboarding',
      s4d: 'We hand over the ready agent, show you how to use it, and provide 30 days of support.',
      link: 'Order Setup'
    },
    pricing: {
      title1: 'One solution. One payment.',
      title2: 'Your agent forever.',
      free: 'Free',
      freeDesc: 'consultation and process audit',
      f1: 'Call with an expert',
      f2: 'Analysis of your routine tasks',
      f3: 'Selection of optimal AI use cases',
      f4: 'Technical feasibility assessment',
      f5: 'Answers to all security questions',
      btnFree: 'Book a call',
      paid: 'Turnkey Setup',
      paidFrom: 'from',
      paidDesc: 'one-time, no hidden subscriptions',
      p1: 'Full OpenClaw installation on your server/PC',
      p2: 'Configuration for your specific business needs',
      p3: 'Messenger integration (Telegram / WhatsApp)',
      p4: 'Installation and setup of required Skills',
      p5: 'Onboarding — you know how to use it from day one',
      p6: '30 days of tech support post-installation',
      btnPaid: 'Order Setup'
    },
    testimonials: {
      title: 'What OpenClaw users say about their agent',
      link: "Want the same? We'll set one up for you.",
      t1: 'After years of AI hype... AI as teammate, not tool. Finally something that actually does the work instead of just generating text.',
      a1: 'Michael',
      h1: '@michael_dev',
      t2: 'No more need to pay a virtual assistant!! OpenClaw handles my emails and scheduling flawlessly. Best investment this year.',
      a2: 'Anna',
      h2: '@anna_biz',
      t3: "Everything Siri was supposed to be. I just text my agent on Telegram and things get done. It's magic.",
      a3: 'Alex',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Frequently Asked Questions',
      link: 'Still have questions? Order Setup',
      q1: 'Is it safe? Will my data leak?',
      a1: "OpenClaw runs locally or on your private server. Data never leaves your perimeter and isn't used to train third-party models.",
      q2: 'Do I need an OpenClaw subscription?',
      a2: 'No. OpenClaw is a completely free open-source project. You only pay for the AI API usage (pennies) and our one-time setup fee.',
      q3: 'Why not set it up myself?',
      a3: "You could. But that's 10-40 hours of figuring out docs, servers, API keys, and prompts. We do it for you, delivering a ready-to-use result.",
      q4: 'What if something breaks?',
      a4: "30 days of tech support are included. If anything goes wrong, we'll fix it quickly and for free.",
      q5: 'What devices does it work on?',
      a5: "The agent can run on Mac, Windows, Linux, or a cloud server. We'll help you pick the best option during the free consultation.",
      q6: 'Do I need to be tech-savvy?',
      a6: 'Absolutely not. After our setup, you just talk to the agent via voice or text in Telegram, just like a normal person.'
    },
    cta: {
      title1: 'Your AI agent could be working',
      title2: 'right now.',
      subtitle: "Leave a request, and we'll get back to you to discuss setting up your personal AI employee.",
      s1: 'Leave a request',
      s1d: 'Fill out the form on the right',
      s2: 'Free audit',
      s2d: 'We hop on a call and break down your tasks',
      s3: 'Turnkey setup',
      s3d: 'We configure the agent and onboard you',
      formTitle: 'Free Consultation',
      formSubtitle: 'Fast response. 100% confidential.'
    },
    form: {
      name: 'Name',
      namePh: 'John Doe',
      phone: 'Phone',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'john@example.com',
      comment: 'Comment (optional)',
      commentPh: 'Briefly describe your tasks...',
      submit: 'Submit Request',
      terms: 'By clicking the button, you agree to the privacy policy.',
      success: 'Request sent successfully!'
    },
    footer: {
      desc: 'Independent integration studio. OpenClaw is an open-source project.'
    },
    badges: {
      conf: 'Confidential',
      fast: 'Fast response'
    }
  },
  ru: {
    seo: {
      title: 'Заказать установку OpenClaw под ключ — настройка ИИ-агента для бизнеса',
      description: 'Заказать установку OpenClaw под ключ для Telegram, WhatsApp, Slack, почты, календаря и файлов. Настройка ИИ-агента под задачи бизнеса, запуск и обучение.',
      h1: 'Заказать установку OpenClaw под ключ',
      h1sub: 'Настраиваем ИИ-агента OpenClaw для Telegram, WhatsApp, Slack, почты, календаря, браузера и файлов под задачи вашего бизнеса.'
    },
    nav: {
      features: 'Возможности',
      howItWorks: 'Как работаем',
      pricing: 'Цены',
      faq: 'FAQ',
      order: 'Заказать установку'
    },
    hero: {
      title1: 'Заказать установку',
      title2: 'OpenClaw',
      title3: 'под ключ',
      subtitle: 'Твой личный цифровой сотрудник в Telegram, WhatsApp или Slack — настроенный, обученный и готовый работать 24/7.',
      btn: 'Заказать установку ИИ-агента'
    },
    whatWeDo: {
      title1: 'Ты получаешь готового ИИ-агента.',
      title2: 'Мы берём на себя всё остальное.',
      withoutYou: 'Без тебя',
      withoutYouDesc: 'Читать сложную документацию, настраивать сервер окружение, разбираться с API ключами и писать системные промпты.',
      we: 'Мы',
      weDesc: 'Устанавливаем ядро, настраиваем интеграции, подключаем мессенджеры, обучаем агента под твои задачи и тестируем.',
      you: 'Ты',
      youDesc: 'Просто открываешь Telegram, пишешь своему новому сотруднику задачу — и он начинает работать.',
      link: 'Хочу готового агента'
    },
    features: {
      title1: 'Что OpenClaw сделает для тебя',
      title2: 'прямо сейчас',
      mail: 'Управление почтой',
      mailDesc: 'Агент сам читает, сортирует и готовит черновики ответов на рутинные письма.',
      cal: 'Календарь',
      calDesc: 'Планирует встречи, учитывая твой график.',
      web: 'Браузер',
      webDesc: 'Ищет инфу, парсит сайты и заполняет формы.',
      msg: 'Мессенджеры',
      msgDesc: 'Установка ИИ-агента для Telegram, WhatsApp, Slack и Discord. Общайся с агентом как с коллегой.',
      files: 'Файлы',
      filesDesc: 'Анализ PDF, таблиц и создание отчетов.',
      home: 'Умный дом',
      homeDesc: 'Управление светом и сценариями.',
      link: 'Заказать установку OpenClaw'
    },
    how: {
      title1: 'От заявки до работающего агента —',
      title2: 'под ключ',
      s1: 'Ты оставляешь заявку',
      s1d: 'Заполняешь короткую форму или пишешь нам в Telegram.',
      s2: 'Бесплатная консультация',
      s2d: 'Мы созваниваемся, чтобы понять твои задачи и процессы.',
      s3: 'Настройка OpenClaw для бизнеса',
      s3d: 'Разворачиваем OpenClaw, подключаем нужные каналы и обучаем агента.',
      s4: 'Сдача и обучение',
      s4d: 'Передаём готового агента, показываем как пользоваться и даём 30 дней поддержки.',
      link: 'Заказать установку'
    },
    pricing: {
      title1: 'Одно решение. Один платёж.',
      title2: 'Навсегда твой агент.',
      free: 'Бесплатно',
      freeDesc: 'консультация и аудит процессов',
      f1: 'Созвон с экспертом',
      f2: 'Анализ твоих рутинных задач',
      f3: 'Подбор оптимальных сценариев для ИИ',
      f4: 'Оценка технической возможности',
      f5: 'Ответы на все вопросы по безопасности',
      btnFree: 'Записаться',
      paid: 'Установка под ключ',
      paidFrom: 'от',
      paidDesc: 'единоразово, без скрытых подписок',
      p1: 'Полная установка OpenClaw на твой сервер/ПК',
      p2: 'Настройка под твои конкретные бизнес-задачи',
      p3: 'Подключение мессенджеров (Telegram / WhatsApp)',
      p4: 'Установка и настройка нужных Skills (навыков)',
      p5: 'Обучение — ты сразу умеешь пользоваться',
      p6: '30 дней технической поддержки после установки',
      btnPaid: 'Заказать установку'
    },
    testimonials: {
      title: 'Пользователи OpenClaw о своём агенте',
      link: 'Хочешь так же? Мы установим тебе такого же.',
      t1: 'После стольких лет хайпа вокруг ИИ... ИИ как напарник, а не просто инструмент. Наконец-то что-то, что реально делает работу, а не просто генерирует текст.',
      a1: 'Михаил',
      h1: '@mikhail_dev',
      t2: 'Больше не нужно платить виртуальному ассистенту!! OpenClaw безупречно справляется с моей почтой и расписанием. Лучшая инвестиция в этом году.',
      a2: 'Анна',
      h2: '@anna_biz',
      t3: 'Всё то, чем должна была стать Siri. Я просто пишу своему агенту в Telegram, и задачи выполняются. Это магия.',
      a3: 'Алекс',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Частые вопросы',
      link: 'Остались вопросы? Заказать установку',
      q1: 'Это безопасно? Мои данные не утекут?',
      a1: 'OpenClaw работает локально или на твоём личном сервере. Данные не покидают твой контур и не используются для обучения чужих моделей.',
      q2: 'Нужна ли подписка на OpenClaw?',
      a2: 'Нет. OpenClaw — это полностью бесплатный open-source проект. Ты платишь только за API используемых нейросетей (копейки) и нашу единоразовую настройку.',
      q3: 'Почему не настроить самому?',
      a3: 'Можно. Но это 10-40 часов разбирательств с документацией, серверами, API ключами и промптами. Мы делаем это за тебя, выдавая готовый результат.',
      q4: 'Что если что-то сломается?',
      a4: 'В стоимость включены 30 дней технической поддержки. Если что-то пойдёт не так — мы оперативно и бесплатно всё починим.',
      q5: 'На каком устройстве работает?',
      a5: 'Агент может работать на Mac, Windows, Linux или облачном сервере. Мы подберём оптимальный вариант на бесплатной консультации.',
      q6: 'Нужно ли быть технарём?',
      a6: 'Абсолютно нет. После нашей настройки ты будешь общаться с агентом просто голосовыми или текстом в Telegram, как с обычным человеком.'
    },
    cta: {
      title1: 'Твой ИИ-агент мог бы работать',
      title2: 'прямо сейчас.',
      subtitle: 'Оставь заявку, и мы свяжемся с тобой, чтобы обсудить установку твоего личного ИИ-сотрудника.',
      s1: 'Оставляешь заявку',
      s1d: 'Заполняешь форму справа',
      s2: 'Бесплатный аудит',
      s2d: 'Созваниваемся и разбираем твои задачи',
      s3: 'Установка под ключ',
      s3d: 'Настраиваем агента и обучаем тебя',
      formTitle: 'Бесплатная консультация',
      formSubtitle: 'Отвечаем быстро. Конфіденциально.'
    },
    form: {
      name: 'Имя',
      namePh: 'Иван Иванов',
      phone: 'Телефон',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'ivan@example.com',
      comment: 'Комментарий (по желанию)',
      commentPh: 'Опишите ваши задачи вкратце...',
      submit: 'Отправить заявку',
      terms: 'Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности',
      success: 'Заявка успешно отправлена!'
    },
    footer: {
      desc: 'Независимая студия интеграции. OpenClaw является open-source проектом.'
    },
    badges: {
      conf: 'Конфиденциально',
      fast: 'Быстрый ответ'
    }
  },
  uk: {
    seo: {
      title: 'Замовити встановлення OpenClaw під ключ — налаштування ШІ-агента для бізнесу',
      description: 'Замовити встановлення OpenClaw під ключ для Telegram, WhatsApp, Slack, пошти, календаря та файлів. Налаштування ШІ-агента під задачі бізнесу, запуск і навчання.',
      h1: 'Замовити встановлення OpenClaw під ключ',
      h1sub: 'Налаштовуємо ШІ-агента OpenClaw для Telegram, WhatsApp, Slack, пошти, календаря, браузера та файлів під задачі вашого бізнесу.'
    },
    nav: {
      features: 'Можливості',
      howItWorks: 'Як працюємо',
      pricing: 'Ціни',
      faq: 'FAQ',
      order: 'Замовити встановлення'
    },
    hero: {
      title1: 'Замовити встановлення',
      title2: 'OpenClaw',
      title3: 'під ключ',
      subtitle: 'Твій особистий цифровий співробітник у Telegram, WhatsApp або Slack — налаштований, навчений і готовий працювати 24/7.',
      btn: 'Замовити встановлення ШІ-агента'
    },
    whatWeDo: {
      title1: 'Ти отримуєш готового ШІ-агента.',
      title2: 'Ми беремо на себе все інше.',
      withoutYou: 'Без тебе',
      withoutYouDesc: 'Читати складну документацію, налаштовувати серверне оточення, розбиратися з API ключами та писати системні промпти.',
      we: 'Ми',
      weDesc: 'Встановлюємо ядро, налаштовуємо інтеграції, підключаємо месенджери, навчаємо агента під твої завдання та тестуємо.',
      you: 'Ти',
      youDesc: 'Просто відкриваєш Telegram, пишеш своєму новому співробітнику завдання — і він починає працювати.',
      link: 'Хочу готового агента'
    },
    features: {
      title1: 'Що OpenClaw зробить для тебе',
      title2: 'прямо зараз',
      mail: 'Керування поштою',
      mailDesc: 'Агент сам читає, сортує та готує чернетки відповідей на рутинні листи.',
      cal: 'Календар',
      calDesc: 'Планує зустрічі, враховуючи твій графік.',
      web: 'Браузер',
      webDesc: 'Шукає інфу, парсить сайти та заповнює форми.',
      msg: 'Месенджери',
      msgDesc: 'Встановлення ШІ-агента для Telegram, WhatsApp, Slack та Discord. Спілкуйся з агентом як із колегою.',
      files: 'Файлы',
      filesDesc: 'Аналіз PDF, таблиць та створення звітів.',
      home: 'Розумний дім',
      homeDesc: 'Керування світлом та сценаріями.',
      link: 'Замовити встановлення OpenClaw'
    },
    how: {
      title1: 'Від заявки до працюючого агента —',
      title2: 'під ключ',
      s1: 'Ти залишаєш заявку',
      s1d: 'Заповнюєш коротку форму або пишеш нам у Telegram.',
      s2: 'Безкоштовна консультация',
      s2d: 'Ми зідзвонюємося, щоб зрозуміти твої завдання та процеси.',
      s3: 'Налаштування OpenClaw для бізнесу',
      s3d: 'Розгортаємо OpenClaw, підключаємо потрібні канали та навчаємо агента.',
      s4: 'Здача та навчання',
      s4d: 'Передаємо готового агента, показуємо як користуватися і даємо 30 днів підтримки.',
      link: 'Замовити встановлення'
    },
    pricing: {
      title1: 'Одне рішення. Один платіж.',
      title2: 'Назавжди твій агент.',
      free: 'Безкоштовно',
      freeDesc: 'консультація та аудит процесів',
      f1: 'Зідзвон з експертом',
      f2: 'Аналіз твоїх рутинних завдань',
      f3: 'Підбір оптимальних сценаріїв для ШІ',
      f4: 'Оцінка технічної можливості',
      f5: 'Відповіді на всі питання щодо безпеки',
      btnFree: 'Записатися',
      paid: 'Встановлення під ключ',
      paidFrom: 'від',
      paidDesc: 'одноразово, без прихованих підписок',
      p1: 'Повне встановлення OpenClaw на твій сервер/ПК',
      p2: 'Налаштування під твої конкретні бізнес-завдання',
      p3: 'Підключення месенджерів (Telegram / WhatsApp)',
      p4: 'Встановлення та налаштування потрібних Skills (навичок)',
      p5: 'Навчання — ти відразу вмієш користуватися',
      p6: '30 днів технічної підтримки після встановлення',
      btnPaid: 'Замовити встановлення'
    },
    testimonials: {
      title: 'Користувачі OpenClaw про свого агента',
      link: 'Хочеш так само? Ми встановимо тобі такого ж.',
      t1: 'Після стількох років хайпу навколо ШІ... ШІ як напарник, а не просто інструмент. Нарешті щось, що реально робить роботу, а не просто генерує текст.',
      a1: 'Михайло',
      h1: '@mykhailo_dev',
      t2: 'Більше не потрібно платити віртуальному асистенту!! OpenClaw бездоганно справляється з моєю поштою та розкладом. Найкраща інвестиція цього року.',
      a2: 'Ганна',
      h2: '@hanna_biz',
      t3: 'Все те, чим мала стати Siri. Я просто пишу своєму агенту в Telegram, і завдання виконуються. Це магія.',
      a3: 'Алекс',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Часті запитання',
      link: 'Залишилися питання? Замовити встановлення',
      q1: 'Це безпечно? Мої дані не витечуть?',
      a1: 'OpenClaw працює локально або на твоєму особистому сервері. Дані не залишають твій контур і не використовуються для навчання чужих моделей.',
      q2: 'Чи потрібна підписка на OpenClaw?',
      a2: 'Ні. OpenClaw — це повністю безкоштовний open-source проєкт. Ти платиш тільки за API використовуваних нейромереж (копійки) та наше одноразове налаштування.',
      q3: 'Чому не налаштувати самому?',
      a3: 'Можна. Але це 10-40 годин розбирань з документацією, серверами, API ключами та промптами. Ми робимо це за тебе, видаючи готовий результат.',
      q4: 'Що якщо щось зламається?',
      a4: 'У вартість включено 30 днів технічної підтримки. Якщо щось піде не так — ми оперативно і безкоштовно все полагодимо.',
      q5: 'На якому пристрої працює?',
      a5: 'Агент може працювати на Mac, Windows, Linux або хмарному сервері. Ми підберемо оптимальний варіант на безкоштовній консультації.',
      q6: 'Чи потрібно бути технарем?',
      a6: 'Абсолютно ні. Після нашого налаштування ти будеш спілкуватися з агентом просто голосовими або текстом у Telegram, як зі звичайною людиною.'
    },
    cta: {
      title1: 'Твій ШІ-агент міг би працювати',
      title2: 'прямо зараз.',
      subtitle: "Залиш заявку, і ми зв'яжемося з тобою, щоб обговорити встановлення твого особистого ШІ-співробітника.",
      s1: 'Залишаєш заявку',
      s1d: 'Заповнюєш форму праворуч',
      s2: 'Безкоштовний аудит',
      s2d: 'Зідзвонюємося і розбираємо твої завдання',
      s3: 'Встановлення під ключ',
      s3d: 'Налаштовуємо агента і навчаємо тебе',
      formTitle: 'Безкоштовна консультація',
      formSubtitle: 'Відповідаємо швидко. Конфіденційно.'
    },
    form: {
      name: "Ім'я",
      namePh: 'Іван Іванов',
      phone: 'Телефон',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'ivan@example.com',
      comment: 'Коментар (за бажанням)',
      commentPh: 'Опишіть ваші завдання коротко...',
      submit: 'Відправити заявку',
      terms: 'Натискаючи кнопку, ви погоджуєтеся з політикою конфіденційності',
      success: 'Заявку успішно відправлено!'
    },
    footer: {
      desc: 'Незалежна студія інтеграції. OpenClaw є open-source проєктом.'
    },
    badges: {
      conf: 'Конфіденційно',
      fast: 'Швидка відповідь'
    }
  }
};
type Lang = 'en' | 'ru' | 'uk';
// --- SEO Hook ---
const useSEO = (lang: Lang, t: any, faqs: any[]) => {
  useEffect(() => {
    // 1. Title & Meta Description
    document.title = t.seo.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t.seo.description);
    // Robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'index, follow');
    // 2. HTML Lang
    document.documentElement.lang = lang === 'uk' ? 'uk' : lang;
    // 3. Canonical & Hreflang
    const baseUrl = 'https://setupclaw.agency';
    const currentPath = lang === 'en' ? '/' : lang === 'ru' ? '/ru' : '/ua';
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${baseUrl}${currentPath}`);
    // Remove old hreflangs
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    const langs = [{
      code: 'en',
      path: '/'
    }, {
      code: 'ru',
      path: '/ru'
    }, {
      code: 'uk',
      path: '/ua'
    }, {
      code: 'x-default',
      path: '/'
    }];
    langs.forEach(({
      code,
      path
    }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', `${baseUrl}${path}`);
      document.head.appendChild(link);
    });
    // 4. Schema Markup
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SetupClaw Agency',
      url: baseUrl,
      logo: `${baseUrl}/logo.png`
    };
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: t.seo.h1,
      description: t.seo.description,
      provider: {
        '@type': 'Organization',
        name: 'SetupClaw Agency'
      },
      offers: {
        '@type': 'Offer',
        price: '500',
        priceCurrency: 'USD'
      }
    };
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f: any) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    };
    const injectSchema = (schema: any) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    };
    injectSchema(organizationSchema);
    injectSchema(serviceSchema);
    injectSchema(faqSchema);
  }, [lang, t, faqs]);
};
// --- Components ---
// Standard FadeIn for most elements
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 15
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  amount: 0.1
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0" data-mp="3jjs5g5xh2rz4">
    {children}
  </motion.div>;
// Special FadeIn for Grid items to prevent layout shifts and jitter on mobile
const BentoFadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.98
}} whileInView={{
  opacity: 1,
  scale: 1
}} viewport={{
  once: true,
  amount: 0.1
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-1" data-mp="28sca3m4vg9kz">
    {children}
  </motion.div>;
const LeadForm = ({
  onSubmit,
  t,
  lang
}: {
  onSubmit?: () => void;
  t: any;
  lang: string;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    comment: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.comment || 'Не указано',
        form_type: 'Заявка с сайта SetupClaw Agency',
        page_url: window.location.href,
        language: lang,
        utm_source: urlParams.get('utm_source') || undefined,
        utm_medium: urlParams.get('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || undefined,
        utm_term: urlParams.get('utm_term') || undefined,
        utm_content: urlParams.get('utm_content') || undefined,
        utm_id: urlParams.get('utm_id') || undefined,
        gclid: urlParams.get('gclid') || undefined,
        fbclid: urlParams.get('fbclid') || undefined,
        landing_page: window.location.pathname,
        referrer: document.referrer || undefined
      };
      await fetch('https://white-bonus-e94a.maxkhmlyt.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Игнорируем ошибки отправки, как просил пользователь
    } finally {
      setIsSubmitting(false);
      onSubmit?.();
    }
  };
  return <form className="space-y-4" onSubmit={handleSubmit} data-id="element-2" data-mp="2y6rx1y8v2rsl">
      <div data-id="element-3" data-mp="32yjjbwkzibcx">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-4" data-mp="32z1m7c25jntt">
          {t.name}
        </label>
        <input type="text" required value={formData.name} onChange={e => setFormData(prev => ({
        ...prev,
        name: e.target.value
      }))} className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.namePh} data-id="element-5" data-mp="2oaevke72r9na" />
      </div>
      <div data-id="element-6" data-mp="2zpa1v65odofc">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-7" data-mp="2znp99m2x8fk4">
          {t.phone}
        </label>
        <input type="tel" required value={formData.phone} onChange={e => setFormData(prev => ({
        ...prev,
        phone: e.target.value
      }))} className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.phonePh} data-id="element-8" data-mp="2prvojff1tfr9" />
      </div>
      <div data-id="element-9" data-mp="272fkwj82h76m">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-10" data-mp="271w2l1ei8gs2">
          {t.email}
        </label>
        <input type="email" required value={formData.email} onChange={e => setFormData(prev => ({
        ...prev,
        email: e.target.value
      }))} className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.emailPh} data-id="element-11" data-mp="2244cmcxf74qi" />
      </div>
      <div data-id="element-12" data-mp="hjqtp8kz076w">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-13" data-mp="hjz8x74kfol9">
          {t.comment}
        </label>
        <textarea rows={3} value={formData.comment} onChange={e => setFormData(prev => ({
        ...prev,
        comment: e.target.value
      }))} className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all resize-none" placeholder={t.commentPh} data-id="element-14" data-mp="miae34jx1ax5" />
      </div>
      <button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-70 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(220,38,38,0.2)] mt-2 flex justify-center items-center" data-id="element-15" data-mp="3man9x2ocdf5">
        {isSubmitting ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-16" data-mp="2lxypgqpbtkp1" /> : t.submit}
      </button>
      <p className="text-xs text-zinc-500 text-center mt-4" data-id="element-17" data-mp="2bzsoqcf9aklr">{t.terms}</p>
    </form>;
};
const LandingPage = ({
  lang
}: {
  lang: Lang;
}) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = translations[lang];
  const faqs = [{
    q: t.faq.q1,
    a: t.faq.a1
  }, {
    q: t.faq.q2,
    a: t.faq.a2
  }, {
    q: t.faq.q3,
    a: t.faq.a3
  }, {
    q: t.faq.q4,
    a: t.faq.a4
  }, {
    q: t.faq.q5,
    a: t.faq.a5
  }, {
    q: t.faq.q6,
    a: t.faq.a6
  }];
  useSEO(lang, t, faqs);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);
  const openModal = () => {
    setIsModalOpen(true);
    setMobileMenuOpen(false);
  };
  const handleLangChange = (newLang: Lang) => {
    if (newLang === 'en') navigate('/');
    if (newLang === 'ru') navigate('/ru');
    if (newLang === 'uk') navigate('/ua');
    setMobileMenuOpen(false);
  };
  const steps = [{
    num: '1',
    title: t.how.s1,
    desc: t.how.s1d
  }, {
    num: '2',
    title: t.how.s2,
    desc: t.how.s2d
  }, {
    num: '3',
    title: t.how.s3,
    desc: t.how.s3d
  }, {
    num: '4',
    title: t.how.s4,
    desc: t.how.s4d
  }];
  const testimonials = [{
    text: t.testimonials.t1,
    author: t.testimonials.a1,
    handle: t.testimonials.h1
  }, {
    text: t.testimonials.t2,
    author: t.testimonials.a2,
    handle: t.testimonials.h2
  }, {
    text: t.testimonials.t3,
    author: t.testimonials.a3,
    handle: t.testimonials.h3
  }];
  return <div className="min-h-screen bg-black text-zinc-50 font-sans selection:bg-red-500/30 overflow-x-hidden" data-id="element-18" data-mp="1io6zljx125pf">
      {/* Navbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isScrolled ? 'bg-black/80 backdrop-blur-md border-white/10 py-3' : 'bg-transparent border-transparent py-5'}`} data-id="element-19" data-mp="1rqpzbv5io27g">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-20" data-mp="3thjnh6tklrr2">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight" data-id="element-21" data-mp="3lyacj95f1kzw">
            <span className="text-2xl" data-id="element-22" data-mp="3h7tybmtrtvch">🦞</span>
            <span data-id="element-23" data-mp="3h72167kizpk3">SetupClaw Agency</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-400" data-id="element-24" data-mp="3c7z3x9asenmo">
            <a href="#features" className="hover:text-white transition-colors" data-id="element-25" data-mp="387yjn810bi7p">
              {t.nav.features}
            </a>
            <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-26" data-mp="8riddo2mizcf">
              {t.nav.howItWorks}
            </a>
            <a href="#pricing" className="hover:text-white transition-colors" data-id="element-27" data-mp="3vvyg8ol022u7">
              {t.nav.pricing}
            </a>
            <a href="#faq" className="hover:text-white transition-colors" data-id="element-28" data-mp="3qx52qw5pqbu1">
              {t.nav.faq}
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-6" data-id="element-29" data-mp="2kf8at7oehf1n">
            {/* Language Switcher */}
            <div className="relative group" data-id="element-30" data-mp="79s4js3bk2gh">
              <button className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-medium py-2" data-id="element-31" data-mp="78qp7bf9stsy">
                <Globe className="w-4 h-4" data-id="element-32" data-mp="c1tdb41a3b1r" />
                <span className="uppercase text-sm" data-id="element-33" data-mp="3uf193f5sr59j">
                  {lang === 'uk' ? 'UA' : lang}
                </span>
                <ChevronDown className="w-3 h-3" data-id="element-34" data-mp="2bqiqzeidp1b" />
              </button>
              <div className="absolute top-full right-0 mt-1 w-24 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl" data-id="element-35" data-mp="pzrpq3w1gxem">
                <button onClick={() => handleLangChange('en')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'en' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-36" data-mp="gyjs7o1ck0ll">
                  EN
                </button>
                <button onClick={() => handleLangChange('ru')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'ru' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-37" data-mp="36qhzmwb0gmfv">
                  RU
                </button>
                <button onClick={() => handleLangChange('uk')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'uk' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-38" data-mp="383zu41nat6vy">
                  UA
                </button>
              </div>
            </div>

            <button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(220,38,38,0.3)]" data-id="element-39" data-mp="2p89rcc7jk6h0">
              {t.nav.order}
            </button>
          </div>

          {/* Mobile Toggle */}
          <div className="md:hidden flex items-center gap-4" data-id="element-40" data-mp="3ru3i38o8sbm8">
            <div className="relative group" data-id="element-41" data-mp="3rtk9wjoe91le">
              <button className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-42" data-mp="nizmdc96c0t">
                <span className="uppercase text-sm" data-id="element-43" data-mp="5e92njx7i2sc">
                  {lang === 'uk' ? 'UA' : lang}
                </span>
              </button>
              <div className="absolute top-full right-0 mt-2 w-24 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" data-id="element-44" data-mp="9i70lrasp7jt">
                <button onClick={() => handleLangChange('en')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'en' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-45" data-mp="3dw5i58ao4hq9">
                  EN
                </button>
                <button onClick={() => handleLangChange('ru')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'ru' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-46" data-mp="x55imtekcnju">
                  RU
                </button>
                <button onClick={() => handleLangChange('uk')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'uk' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-47" data-mp="17an6fidrib15">
                  UA
                </button>
              </div>
            </div>
            <button className="text-zinc-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-48" data-mp="tj08zf832wgs">
              <Menu className="w-6 h-6" data-id="element-49" data-mp="jk16yfhtpz2r" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-50" data-mp="fixxqbua71ai">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-black border-b border-white/10 overflow-hidden" data-id="element-51" data-mp="fkimtlxtts0z">
              <div className="flex flex-col px-6 py-4 gap-4" data-id="element-52" data-mp="2jff8973trnzo">
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-53" data-mp="2jf5ed8fnswop">
                  {t.nav.features}
                </a>
                <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-54" data-mp="2egavy7llta54">
                  {t.nav.howItWorks}
                </a>
                <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-55" data-mp="25gnu5koyq2bj">
                  {t.nav.pricing}
                </a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-56" data-mp="3nrchf0js1ig">
                  {t.nav.faq}
                </a>
                <button onClick={openModal} className="bg-red-600 text-white px-4 py-3 rounded-lg font-medium w-full mt-2" data-id="element-57" data-mp="8ra7tp5jochl">
                  {t.nav.order}
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6 relative overflow-hidden" data-id="element-58" data-mp="rijkg7am3hde">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[800px] md:h-[800px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-59" data-mp="15jnlkla3a3lg" />

        <div className="container mx-auto max-w-4xl text-center relative z-10" data-id="element-60" data-mp="37bs7ng4wq6o9">
          <FadeIn data-id="element-61" data-mp="339nk8z51y6a2">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-[1.1]" data-id="element-62" data-mp="33a6xi2jrc6hr">
              {t.hero.title1}{' '}
              <span className="text-red-500" data-id="element-63" data-mp="1ikanfpb3o1t8">{t.hero.title2}</span>{' '}
              {t.hero.title3}
            </h1>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-64" data-mp="1rje6ita9oh7q">
            <p className="text-xl md:text-2xl text-zinc-400 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-65" data-mp="1rjnwwhz84qdy">
              {t.seo.h1sub}
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="flex flex-col items-center gap-4" data-id="element-66" data-mp="21giu8xjrgkqn">
            <button onClick={openModal} className="group relative inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(220,38,38,0.4)]" data-id="element-67" data-mp="1whz7iuxcf4x8">
              <span className="text-2xl" data-id="element-68" data-mp="2k6s7d2h35wx4">🦞</span>
              {t.hero.btn}
            </button>
          </FadeIn>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-69" data-mp="2yu91ocx423sq">
        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-70" data-mp="2ysfsoqz0z7hk">
          <FadeIn data-id="element-71" data-mp="33stq7f302z1c">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 max-w-4xl mx-auto leading-tight" data-id="element-72" data-mp="33r97cmq1t4jb">
              {t.whatWeDo.title1}
              <br data-id="element-73" data-mp="2tumlbulzk7fp" />
              <span className="text-zinc-500" data-id="element-74" data-mp="2tu4fq7dfogv4">{t.whatWeDo.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-75" data-mp="2g2zgv7hw3slu">
            <FadeIn delay={0.1} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-76" data-mp="2ku7pw2fb5byh">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-77" data-mp="2b3wmcgzjd4s4" />
              <div className="relative z-10" data-id="element-78" data-mp="1x79ehj61ou7f">
                <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-79" data-mp="222yf7ptswh5b">
                  <X className="w-7 h-7 text-red-500" data-id="element-80" data-mp="2wj9uja1nxg3" />
                </div>
                <h3 className="text-xl font-bold mb-4" data-id="element-81" data-mp="3u3cb1n4u7wkm">
                  {t.whatWeDo.withoutYou}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-82" data-mp="3q2al5wwt8wvb">
                  {t.whatWeDo.withoutYouDesc}
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden shadow-[0_0_30px_rgba(220,38,38,0.05)] group" data-id="element-83" data-mp="qk9lwrrrh34a">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/10 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" data-id="element-84" data-mp="cm2tvs9htxcj" />
              <div className="relative z-10" data-id="element-85" data-mp="2xdd6e3rt5i7b">
                <div className="w-14 h-14 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 border border-red-500/20" data-id="element-86" data-mp="2xbij4it2de02">
                  <Check className="w-7 h-7 text-red-500" data-id="element-87" data-mp="1hu5emstddzv4" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-white" data-id="element-88" data-mp="1hs1dujqrpgz2">
                  {t.whatWeDo.we}
                </h3>
                <p className="text-zinc-300 leading-relaxed" data-id="element-89" data-mp="1qmfr6grqmhfj">
                  {t.whatWeDo.weDesc}
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-90" data-mp="1vl0pubrcct1v">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-91" data-mp="25hm0pdq1kydm" />
              <div className="relative z-10" data-id="element-92" data-mp="b70gjuk97ief">
                <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-93" data-mp="f7am5644w6sr">
                  <span className="text-2xl" data-id="element-94" data-mp="3g0ox8km78197">🎯</span>
                </div>
                <h3 className="text-xl font-bold mb-4" data-id="element-95" data-mp="3c04yjclz8pky">{t.whatWeDo.you}</h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-96" data-mp="3q0xttzk3rop4">
                  {t.whatWeDo.youDesc}
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} className="mt-12 text-center" data-id="element-97" data-mp="2xc0f26f3oa14">
            <button onClick={openModal} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-semibold text-lg transition-colors group" data-id="element-98" data-mp="2xca4nsw8gf6a">
              {t.whatWeDo.link}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-99" data-mp="32bd8xb4e2hd7" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Features (Bento Grid) */}
      <section id="features" className="py-24 px-6 relative overflow-hidden" data-id="element-100" data-mp="29mpil14s91ss">
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-101" data-mp="29klh0pucwevo">
          <FadeIn data-id="element-102" data-mp="p2u7lqocy6wx">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-103" data-mp="p2aow7qvvehu">
              {t.features.title1}{' '}
              <span className="text-red-500" data-id="element-104" data-mp="g3p9umkmehlt">{t.features.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6" data-id="element-105" data-mp="k511syqyvex6">
            <BentoFadeIn delay={0.1} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-106" data-mp="k37v5jgcsup6">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-107" data-mp="yuq6h3y9q127" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-108" data-mp="13v4eilzvdb08">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-109" data-mp="3jiuofkp6prtv">
                  <Mail className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-110" data-mp="1u2sfb3mlupqm" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-111" data-mp="1p6aak3r9yrea">
                  {t.features.mail}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-112" data-mp="1gc5e94tdgnrd">
                  {t.features.mailDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.2} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-113" data-mp="1bag2to28m82r">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-114" data-mp="xdt53j2gndvs" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-115" data-mp="2vqbb2nhflbda">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-116" data-mp="2rrkag6drbblf">
                  <Calendar className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-117" data-mp="igtdzzdrndqc" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-118" data-mp="nbgupc2j33ud">
                  {t.features.cal}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-119" data-mp="3qqct1ebunl9x">
                  {t.features.calDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.3} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-120" data-mp="3vr0ku27wk657">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-121" data-mp="1f0sdspa3ltku" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-122" data-mp="1k16ka2w1wvgr">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-123" data-mp="vnbc76ef2yi7">
                  <Globe className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-124" data-mp="14gocitet92eb" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-125" data-mp="zmk5yb9giwdr">
                  {t.features.web}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-126" data-mp="19egfcigzu8xa">
                  {t.features.webDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.4} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-127" data-mp="1nbva82q0n8wn">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-128" data-mp="3l48zod8mm1n0" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-129" data-mp="3b1wxuumrezx7">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-130" data-mp="3f046t3azewxu">
                  <MessageSquare className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-131" data-mp="367sok4q0ufsy" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-132" data-mp="2re62bibsja65">
                  {t.features.msg}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-133" data-mp="2ibemz7fsnu98">
                  {t.features.msgDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.5} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-134" data-mp="2nc2nqkttrpgn">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-135" data-mp="1nbahjqvejtxy" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-136" data-mp="1s79ew3ypkmai">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-137" data-mp="1x745e6ekn9v1">
                  <FileText className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-138" data-mp="260h79xum5lt9" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-139" data-mp="2b1wmh9a40y3q">
                  {t.features.files}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-140" data-mp="2kysyvq34q4dk">
                  {t.features.filesDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.6} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-141" data-mp="qk8kfv5ej52m">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-142" data-mp="2z2qlrhbooqon" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-143" data-mp="2q33g2jbx8yps">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-144" data-mp="2q4y4i7mej8ym">
                  <Home className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-145" data-mp="3iskatnn0krp6" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-146" data-mp="38tl7mkl0po9k">
                  {t.features.home}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-147" data-mp="22dsqly00uqqy">
                  {t.features.homeDesc}
                </p>
              </div>
            </BentoFadeIn>
          </div>

          <FadeIn delay={0.7} className="mt-20 text-center" data-id="element-148" data-mp="1yvuxvksew2xy">
            <button onClick={openModal} className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center gap-2" data-id="element-149" data-mp="1ywee8x2eyj9v">
              {t.features.link} <ArrowRight className="w-5 h-5" data-id="element-150" data-mp="1ut8apx6or673" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* How we work */}
      <section id="how-it-works" className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-151" data-mp="1g30sniklbp6x">
        <div className="absolute top-1/2 left-0 w-[250px] h-[250px] md:w-[500px] md:h-[500px] bg-red-600/5 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-152" data-mp="174p3zm3ab1h4" />
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-153" data-mp="1b3xzavdz37es">
          <FadeIn data-id="element-154" data-mp="x7kebca8cnvk">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-155" data-mp="x717aqm4bjwb">
              {t.how.title1}{' '}
              <span className="text-red-500" data-id="element-156" data-mp="1276thr9mce70">{t.how.title2}</span>
            </h2>
          </FadeIn>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-7 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-red-500/40 before:to-transparent" data-id="element-157" data-mp="ildn55fx0fdg">
            {steps.map((step, idx) => <FadeIn key={idx} delay={idx * 0.1} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-158" data-mp="11i53b605ueda" data-mpi={idx}>
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl border-4 border-black bg-red-600 text-white font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_0_4px_rgba(0,0,0,1)] z-10" data-id="element-159" data-mp="1b8hon0fsubkh">
                  {step.num}
                </div>
                <div className="w-[calc(100%-4.5rem)] md:w-[calc(50%-3.5rem)] p-6 rounded-3xl bg-black border border-red-500/40 group-hover:border-red-400 transition-colors duration-300 relative overflow-hidden" data-id="element-160" data-mp="1g8vv4e1r5dge">
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-161" data-mp="3ena5rs6qkxqv" />
                  <div className="relative z-10" data-id="element-162" data-mp="3odwc0vskcbww">
                    <h3 className="font-bold text-xl mb-2 text-white" data-id="element-163" data-mp="3ogqwgnbktwqb">
                      {step.title}
                    </h3>
                    <p className="text-zinc-400" data-id="element-164" data-mp="2vpqs2b9c2qy4">{step.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-165" data-mp="30rg1xnktkykm">
            <button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105" data-id="element-166" data-mp="34rqa96w3412n">
              {t.how.link}
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 relative overflow-hidden" data-id="element-167" data-mp="24kuww4afuvq7">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-168" data-mp="20houx8uczqpj" />

        <div className="container mx-auto max-w-5xl relative z-10" id="order" data-id="element-169" data-mp="1qr3m6skz15sw">
          <FadeIn data-id="element-170" data-mp="1lt252d73vgk2">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-171" data-mp="1ltk93w00ez2j">
              {t.pricing.title1}
              <br data-id="element-172" data-mp="1gvaebmi760hg" />
              <span className="text-red-500" data-id="element-173" data-mp="1crkt970qe116">{t.pricing.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-174" data-mp="17srb31ai1luo">
            {/* Free Tier */}
            <FadeIn delay={0.1} data-id="element-175" data-mp="17uu8w7hw8j9y">
              <div className="bg-black rounded-3xl border border-red-500/40 p-8 md:p-10 shadow-[0_0_50px_rgba(220,38,38,0.02)] relative overflow-hidden group hover:border-red-400 transition-all duration-500 h-full flex flex-col" data-id="element-176" data-mp="375vi3fhbsv6y">
                <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-177" data-mp="3l9kqjae4044q" />

                <div className="relative z-10 flex-grow" data-id="element-178" data-mp="3cb8p0dbpflxi">
                  <div className="mb-8" data-id="element-179" data-mp="3c8mgobgzp2e8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-zinc-900 rounded-2xl mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-180" data-mp="3h8r2mh0o2pbu">
                      <span className="text-2xl" data-id="element-181" data-mp="cvu6nyffrk1d">💬</span>
                    </div>
                    <h3 className="text-2xl font-bold text-zinc-400 tracking-wide uppercase mb-2" data-id="element-182" data-mp="3v177zoh1izfx">
                      {t.pricing.free}
                    </h3>
                    <div className="flex items-baseline gap-2" data-id="element-183" data-mp="3st2i3a4s30c">
                      <span className="text-5xl font-extrabold text-white" data-id="element-184" data-mp="2jkqnvhujjumq">
                        $0
                      </span>
                    </div>
                    <p className="text-zinc-500 mt-2 font-medium" data-id="element-185" data-mp="2oiihqheqxqtk">
                      {t.pricing.freeDesc}
                    </p>
                  </div>

                  <div className="space-y-4 mb-10" data-id="element-186" data-mp="2c1hehom1bvxo">
                    {[t.pricing.f1, t.pricing.f2, t.pricing.f3, t.pricing.f4, t.pricing.f5].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-187" data-mp="1xe60zh6am1ux" data-mpi={idx}>
                        <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0 border border-red-500/20" data-id="element-188" data-mp="1tckkllujv5fz">
                          <Check className="w-4 h-4 text-red-500" data-id="element-189" data-mp="2747pga2k5mad" />
                        </div>
                        <span className="text-zinc-300" data-id="element-190" data-mp="22belhoewyhzc">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="relative z-10 mt-auto" data-id="element-191" data-mp="19o20hioeii85">
                  <button onClick={openModal} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_30px_rgba(220,38,38,0.3)] flex items-center justify-center gap-2" data-id="element-192" data-mp="19n8z6x87okxe">
                    {t.pricing.btnFree}
                  </button>
                </div>
              </div>
            </FadeIn>

            {/* Paid Tier */}
            <FadeIn delay={0.2} data-id="element-193" data-mp="180b66d4okdk8">
              <div className="bg-black rounded-3xl border border-red-500/40 p-8 md:p-10 shadow-[0_0_50px_rgba(220,38,38,0.05)] relative overflow-hidden group hover:border-red-400 transition-all duration-500 h-full flex flex-col" data-id="element-194" data-mp="1c2eljcuh02as">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/20 via-black to-black opacity-60 pointer-events-none transition-opacity duration-500 group-hover:opacity-100" data-id="element-195" data-mp="1lshbt5a7j25f" />

                <div className="relative z-10 flex-grow" data-id="element-196" data-mp="1vqndpmw0k8aa">
                  <div className="mb-8" data-id="element-197" data-mp="3g8ptce7a05dg">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-red-500/10 rounded-2xl mb-6 border border-red-500/20" data-id="element-198" data-mp="3g9r7wsni39zx">
                      <span className="text-2xl" data-id="element-199" data-mp="369qicoz1fxna">🦞</span>
                    </div>
                    <h3 className="text-2xl font-bold text-red-400 tracking-wide uppercase mb-2" data-id="element-200" data-mp="31b6x6qs9qpvz">
                      {t.pricing.paid}
                    </h3>
                    <div className="flex items-baseline gap-2" data-id="element-201" data-mp="2t05bq51gkc3">
                      <span className="text-zinc-400 text-xl" data-id="element-202" data-mp="3tzkvz8r6lq8l">
                        {t.pricing.paidFrom}
                      </span>
                      <span className="text-5xl font-extrabold text-white" data-id="element-203" data-mp="3p7b9k2p9z8b2">
                        $500
                      </span>
                    </div>
                    <p className="text-zinc-500 mt-2 font-medium" data-id="element-204" data-mp="lkavu66uqv3g">
                      {t.pricing.paidDesc}
                    </p>
                  </div>

                  <div className="space-y-4 mb-10" data-id="element-205" data-mp="grhrhjfaporw">
                    {[t.pricing.p1, t.pricing.p2, t.pricing.p3, t.pricing.p4, t.pricing.p5, t.pricing.p6].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-206" data-mp="1tex8jh0mkxe5" data-mpi={idx}>
                        <div className="mt-1 bg-red-500/20 p-1 rounded-full shrink-0 border border-red-500/30" data-id="element-207" data-mp="2q4mvs16oecxd">
                          <Check className="w-4 h-4 text-red-500" data-id="element-208" data-mp="2m3b13h8y31ib" />
                        </div>
                        <span className="text-zinc-300" data-id="element-209" data-mp="2hc2v6v6tpy9w">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="relative z-10 mt-auto" data-id="element-210" data-mp="2cb5dsktn189g">
                  <button onClick={openModal} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_30px_rgba(220,38,38,0.3)] flex items-center justify-center gap-2" data-id="element-211" data-mp="39svrgl7fh11m">
                    <span className="text-xl" data-id="element-212" data-mp="3hcemxqxuymr3">🦞</span> {t.pricing.btnPaid}
                  </button>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-213" data-mp="3vc7bpgptqswf">
        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-214" data-mp="45mqweqd4h7u">
          <FadeIn data-id="element-215" data-mp="43s56y79ol2p">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-216" data-mp="43a0dd6jgyj6">
              {t.testimonials.title}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-217" data-mp="duee69khadcf">
            {testimonials.map((t_item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="bg-black border border-red-500/40 p-8 rounded-3xl flex flex-col hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-218" data-mp="hycc4gy2hi3w" data-mpi={idx}>
                <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-219" data-mp="2ey722qel62o2" />
                <div className="relative z-10 flex flex-col h-full" data-id="element-220" data-mp="2sqwtjacvvzsk">
                  <div className="flex items-center gap-4 mb-6" data-id="element-221" data-mp="2nwslsovsnnqf">
                    <div className="w-14 h-14 bg-zinc-900 border border-white/10 rounded-2xl flex items-center justify-center font-bold text-xl text-zinc-400 group-hover:border-red-500/30 transition-colors" data-id="element-222" data-mp="32mgmpp02ckdm">
                      {t_item.author[0].toUpperCase()}
                    </div>
                    <div data-id="element-223" data-mp="36obmgp2cippa">
                      <div className="font-bold text-white" data-id="element-224" data-mp="36q4ua7op3fyv">
                        {t_item.author}
                      </div>
                      <div className="text-zinc-500 text-sm" data-id="element-225" data-mp="1hbzows47v203">
                        {t_item.handle}
                      </div>
                    </div>
                  </div>
                  <p className="text-zinc-300 leading-relaxed flex-grow" data-id="element-226" data-mp="3nzgx2288iums">
                    "{t_item.text}"
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-227" data-mp="3f5c7rn8r1r9d">
            <p className="text-xl text-zinc-400 mb-6" data-id="element-228" data-mp="3j6xk97hdg3j1">{t.testimonials.link}</p>
            <button onClick={openModal} className="inline-flex items-center gap-2 text-white hover:text-red-400 font-semibold text-lg transition-colors group" data-id="element-229" data-mp="357o427t619f2">
              {t.nav.order}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-230" data-mp="2w818w3r14zug" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 relative overflow-hidden" data-id="element-231" data-mp="uf3e42dozx6v">
        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-232" data-mp="yh83gox3y7np">
          <FadeIn data-id="element-233" data-mp="ygf5acc7sqh6">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-234" data-mp="yevomkmncdev">
              {t.faq.title}
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-235" data-mp="4r7pqvsc4u0d">
            {faqs.map((faq, idx) => <FadeIn key={idx} delay={idx * 0.05} data-id="element-236" data-mp="3vxik692uawiq" data-mpi={idx}>
                <div className="border border-red-500/40 rounded-2xl bg-black overflow-hidden hover:border-red-400 transition-colors duration-300" data-id="element-237" data-mp="3vw7icesyfyu9">
                  <button className="w-full px-6 py-5 text-left flex items-center justify-between font-semibold text-lg hover:bg-red-500/5 transition-colors text-white" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-id="element-238" data-mp="3mwu6xgl9t06w">
                    <span data-id="element-239" data-mp="3887sdh3gwcgq">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-240" data-mp="38991hg0xaovt" />
                  </button>
                  <motion.div initial={false} animate={{
                height: openFaq === idx ? 'auto' : 0,
                opacity: openFaq === idx ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-241" data-mp="2z81gfznf3kjo">
                    <div className="px-6 pb-5 text-zinc-400 leading-relaxed border-t border-red-500/40 pt-4" data-id="element-242" data-mp="1soqa8hg2okyb">
                      {faq.a}
                    </div>
                  </motion.div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.3} className="mt-12 text-center" data-id="element-243" data-mp="14ze3rpc4qqzp">
            <button onClick={openModal} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-semibold text-lg transition-colors group" data-id="element-244" data-mp="14xun3xmkadxe">
              {t.faq.link}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-245" data-mp="h9befgzlnz4s" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 md:px-6 bg-black relative overflow-hidden border-t border-white/10" data-id="element-246" data-mp="581wq2qsq1m8">
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-247" data-mp="izq8g4x4uo2o" />

        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-248" data-mp="3mf5j98dc51ov">
          <div className="bg-zinc-950 border border-red-500/40 rounded-3xl p-5 md:p-16 shadow-2xl overflow-hidden relative" data-id="element-249" data-mp="3iebvcezw7mvz">
            <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent pointer-events-none" data-id="element-250" data-mp="8z4jivwzpvo" />

            <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center relative z-10" data-id="element-251" data-mp="1bn2xclqrn7z5">
              <FadeIn data-id="element-252" data-mp="16pkmgpt6uioi">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-2xl mb-8 border border-red-500/20" data-id="element-253" data-mp="16nzu96ucjbtt">
                  <span className="text-4xl" data-id="element-254" data-mp="2tlhelg8k4re9">🦞</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight text-white" data-id="element-255" data-mp="2tmsirh5x015w">
                  {t.cta.title1}{' '}
                  <span className="text-red-500" data-id="element-256" data-mp="2kkj72nywu1qx">{t.cta.title2}</span>
                </h2>
                <p className="text-zinc-400 text-lg mb-10 leading-relaxed" data-id="element-257" data-mp="38adhkyvrbe80">
                  {t.cta.subtitle}
                </p>

                <div className="space-y-6" data-id="element-258" data-mp="3cc8n6fkq0drl">
                  <div className="flex items-start gap-4" data-id="element-259" data-mp="3ce347isvflqe">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-260" data-mp="2yjiz85lmsyru">
                      <span className="text-zinc-400 font-bold" data-id="element-261" data-mp="33hkfyjvl4m05">1</span>
                    </div>
                    <div data-id="element-262" data-mp="3r76d71secr5i">
                      <h3 className="text-white font-bold mb-1" data-id="element-263" data-mp="3vzzl1yj5wflt">{t.cta.s1}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-264" data-mp="3w22eytnfqsvt">{t.cta.s1d}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-265" data-mp="1r79tqfpac7b8">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-266" data-mp="1r5oyenv5ckcb">
                      <span className="text-zinc-400 font-bold" data-id="element-267" data-mp="1h8tyq1n50dwg">2</span>
                    </div>
                    <div data-id="element-268" data-mp="2ewkkd4cx8lbn">
                      <h3 className="text-white font-bold mb-1" data-id="element-269" data-mp="2ewuft7gqjkoq">{t.cta.s2}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-270" data-mp="29xr97il3x6en">{t.cta.s2d}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-271" data-mp="24xn1agy9lk01">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-272" data-mp="25095c6ly5hdi">
                      <span className="text-zinc-400 font-bold" data-id="element-273" data-mp="tjhttdyuerzy">3</span>
                    </div>
                    <div data-id="element-274" data-mp="pheh6m0fthd3">
                      <h3 className="text-white font-bold mb-1" data-id="element-275" data-mp="pg323q1jkn6o">{t.cta.s3}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-276" data-mp="316156td0p2rw">{t.cta.s3d}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-277" data-mp="3a5o9bmx4smon">
                <div className="bg-black border border-red-500/40 rounded-3xl p-5 md:p-8 shadow-[0_0_50px_rgba(220,38,38,0.05)] relative group hover:border-red-400 transition-colors duration-500" data-id="element-278" data-mp="3a4mur8gwpi26">
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl" data-id="element-279" data-mp="3jufw7gnc4gsp" />
                  <div className="relative z-10" data-id="element-280" data-mp="3svnu3xlxvfm9">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-281" data-mp="3swyudng6e58m">
                      {t.cta.formTitle}
                    </h3>
                    <p className="text-zinc-400 mb-6 text-sm" data-id="element-282" data-mp="1yieedo2suxvk">
                      {t.cta.formSubtitle}
                    </p>
                    <LeadForm onSubmit={() => alert(t.form.success)} t={t.form} lang={lang} data-id="element-283" data-mp="23iairofesrbn" />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 bg-black" data-id="element-284" data-mp="1f6of09m8j1sa">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-285" data-mp="1k4znafwvt48e">
          <div className="flex items-center gap-2 font-bold text-xl text-white" data-id="element-286" data-mp="27utykt1jykqj">
            <span data-id="element-287" data-mp="2csvjljijgu4n">🦞</span>
            <span data-id="element-288" data-mp="2cs3mu5d7gqcs">SetupClaw Agency</span>
          </div>

          <div className="flex items-center gap-6" data-id="element-289" data-mp="1ytdo6laqmimb">
            <a href="#" className="text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-290" data-mp="1yskscfdbhdiy">
              Telegram
            </a>
            <a href="#" className="text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-291" data-mp="ncw5do02cei5">
              WhatsApp
            </a>
          </div>

          <div className="text-zinc-600 text-sm text-center md:text-right max-w-xs" data-id="element-292" data-mp="jughkhhj1iit">
            {t.footer.desc}
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-293" data-mp="tuh0w3febyn0">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} data-id="element-294" data-mp="truwgcnsxz90">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-red-500/40 rounded-3xl p-6 md:p-8 w-full max-w-md relative shadow-2xl" data-id="element-295" data-mp="awe95gvgrnli">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors" data-id="element-296" data-mp="bx6ceinljfuo">
                <X className="w-6 h-6" data-id="element-297" data-mp="3gb4wo7eut44t" />
              </button>

              <div className="mb-8" data-id="element-298" data-mp="3kbyhut0ww4u0">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-299" data-mp="3kczu30tnyxdb">
                  {t.cta.formTitle}
                </h3>
                <p className="text-zinc-400" data-id="element-300" data-mp="3p9gjrzgihjsi">{t.cta.subtitle}</p>
              </div>

              <LeadForm onSubmit={() => {
            alert(t.form.success);
            setIsModalOpen(false);
          }} t={t.form} lang={lang} data-id="element-301" data-mp="3uaxhxn7m2s5e" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function App() {
  return <BrowserRouter data-id="element-302" data-mp="2p2v0avj175r1">
      <Routes data-id="element-303" data-mp="2p2l4usf7w6dy">
        <Route path="/" element={<LandingPage lang="en" data-id="element-305" data-mp="2p3msdwdf6lck" />} data-id="element-304" data-mp="2p3wbr13ylu9a" />
        <Route path="/ru" element={<LandingPage lang="ru" data-id="element-307" data-mp="2kbcyk596p150" />} data-id="element-306" data-mp="2kbv11jmfwbld" />
        <Route path="/ua" element={<LandingPage lang="uk" data-id="element-309" data-mp="2fd1mrozbwgk5" />} data-id="element-308" data-mp="2kablxwcis6l6" />
        <Route path="*" element={<Navigate to="/" replace data-id="element-311" data-mp="2fcicmufx6wgo" />} data-id="element-310" data-mp="2fc0a5g2nzm0b" />
      </Routes>
    </BrowserRouter>;
}